// ===========================================
// LIVE CASINO STATS COMPONENT
// ===========================================
// Animated stats display for the welcome page

import { useState, useEffect, useRef, ReactNode } from 'react';
import { api } from '../api/client';
import type { LiveCasinoStats as LiveCasinoStatsType } from '../api/client';
import { BahtSymbol } from './Currency';
import { useSocketStore } from '../stores/socketStore';

// Animated counter that counts up to a value
function AnimatedNumber({ value, prefix = '', suffix = '', duration = 1500 }: {
  value: number;
  prefix?: ReactNode;
  suffix?: ReactNode;
  duration?: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const startValueRef = useRef(0);

  useEffect(() => {
    startValueRef.current = displayValue;
    startTimeRef.current = null;

    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const current = Math.round(startValueRef.current + (value - startValueRef.current) * easeOut);
      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  const formatted = displayValue.toLocaleString();
  return <span>{prefix}{formatted}{suffix}</span>;
}

// Pulsing dot for "live" indicator - color reflects connection status
function PulsingDot({ isConnected }: { isConnected: boolean }) {
  if (isConnected) {
    return (
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
      </span>
    );
  }
  // Disconnected - red/gray, no pulse
  return (
    <span className="relative flex h-2 w-2">
      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
    </span>
  );
}

// Animated dots for "reconnecting" text
function AnimatedDots() {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-block w-[12px] text-left">
      {'.'.repeat(dotCount)}
    </span>
  );
}

export function LiveCasinoStats() {
  const [stats, setStats] = useState<LiveCasinoStatsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const { isConnected, isReconnecting } = useSocketStore();

  useEffect(() => {
    const fetchStats = async () => {
      const result = await api.analytics.getLiveStats();
      if (result.success && result.data) {
        setStats(result.data);
        setIsLoading(false);
        // Trigger animation after a small delay
        setTimeout(() => setShowStats(true), 100);
      } else {
        setIsLoading(false);
      }
    };

    fetchStats();

    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="card ">
        <div className="card-header">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <span className="animate-pulse">🎰</span> Casino Stats
          </h2>
        </div>
        <div className="card-body">
          <div className="flex items-center justify-center py-8">
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-3 h-3 bg-casino-gold-500 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="card  overflow-hidden">
      {/* Header with live indicator */}
      <div className="card-header flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          🎰 Casino Floor
        </h2>
        <div className={`flex items-center gap-2 text-xs ${
          isConnected ? 'text-green-400' : isReconnecting ? 'text-yellow-400' : 'text-red-400'
        }`}>
          <PulsingDot isConnected={isConnected} />
          <span>
            {isConnected ? 'LIVE' : isReconnecting ? <>Reconnecting<AnimatedDots /></> : 'OFFLINE'}
          </span>
        </div>
      </div>

      <div className="card-body">
        {/* Main stats grid - 2 columns, 2 rows */}
        <div className={`grid grid-cols-2 gap-3 transition-all duration-700 ${
          showStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          {/* Row 1: Playing Now | Blackjacks Today */}
          {/* Players Online */}
          <div className="text-center p-3 bg-gradient-to-br from-green-900/30 to-green-800/10 rounded-xl border border-green-800/30">
            <div className="text-2xl font-bold text-green-400 font-mono">
              <AnimatedNumber value={stats.playersOnlineNow} />
            </div>
            <div className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1">
              <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
              Playing Now
            </div>
          </div>

          {/* Blackjacks Today */}
          <div className="text-center p-3 bg-gradient-to-br from-purple-900/30 to-purple-800/10 rounded-xl border border-purple-800/30">
            <div className="text-2xl font-bold text-purple-400 font-mono">
              <AnimatedNumber value={stats.blackjacksToday} />
            </div>
            <div className="text-xs text-gray-400 mt-1">Blackjacks Today</div>
          </div>

          {/* Row 2: Hands Today | Wagered Today */}
          {/* Hands Today */}
          <div className="text-center p-3 bg-gradient-to-br from-blue-900/30 to-blue-800/10 rounded-xl border border-blue-800/30">
            <div className="text-2xl font-bold text-blue-400 font-mono">
              <AnimatedNumber value={stats.handsToday} />
            </div>
            <div className="text-xs text-gray-400 mt-1">Hands Today</div>
          </div>

          {/* Wagered Today */}
          <div className="text-center p-3 bg-gradient-to-br from-yellow-900/30 to-yellow-800/10 rounded-xl border border-yellow-800/30">
            <div className="text-xl font-bold text-yellow-400 font-mono">
              <AnimatedNumber value={stats.wageredToday} prefix={<BahtSymbol />} />
            </div>
            <div className="text-xs text-gray-400 mt-1">Chips Played Today</div>
          </div>
        </div>

        {/* Biggest Win Banner */}
        {stats.biggestWinToday && (
          <div className={`mt-4 p-3 bg-gradient-to-r from-casino-gold-900/30 via-casino-gold-800/20 to-casino-gold-900/30 rounded-xl border border-casino-gold-700/30 transition-all duration-700 delay-300 ${
            showStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏆</span>
                <div>
                  <div className="text-xs text-gray-400">Biggest Hand Today</div>
                  <div className="font-semibold text-white">
                    {stats.biggestWinToday.username}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-casino-gold-400 font-mono">
                  +<BahtSymbol /><AnimatedNumber value={stats.biggestWinToday.amount} duration={2000} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Play-money reminder */}
        <div className="mt-3 text-center text-[10px] text-gray-500">
          Play chips only — no cash value.
        </div>
      </div>
    </div>
  );
}

export default LiveCasinoStats;
